import { useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, FolderKanban, Ticket, FileText, Bell, User, LogOut, Home } from "lucide-react";
import Logo from "@/components/brand/Logo";
import ThemeToggle from "@/components/site/ThemeToggle";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/dashboard/projects", label: "Projects", icon: FolderKanban },
  { to: "/dashboard/quotes", label: "Quotes", icon: FileText },
  { to: "/dashboard/proposals", label: "Proposals", icon: FileText },
  { to: "/dashboard/tickets", label: "Support", icon: Ticket },
  { to: "/dashboard/notifications", label: "Notifications", icon: Bell },
  { to: "/dashboard/profile", label: "Profile", icon: User },
];

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  useEffect(() => { const el = document.getElementById("portal-main"); if (el) el.scrollTop = 0; }, [pathname]);

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-border bg-card/40 p-4 lg:flex">
        <Logo className="px-2 py-2" />
        <nav className="mt-6 flex-1 space-y-1">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={`portal-nav-${l.label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
              <l.icon className="h-4.5 w-4.5" /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="space-y-1 border-t border-border pt-3">
          <NavLink to="/" className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-secondary"><Home className="h-4 w-4" /> Back to site</NavLink>
          <button onClick={doLogout} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-secondary" data-testid="portal-logout"><LogOut className="h-4 w-4" /> Log out</button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bs-glass px-5">
          <div className="lg:hidden"><Logo compact /></div>
          <div className="hidden text-sm text-muted-foreground lg:block">Welcome back, <span className="font-semibold text-foreground">{user?.name?.split(" ")[0]}</span></div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button size="sm" variant="outline" className="lg:hidden" onClick={doLogout}><LogOut className="h-4 w-4" /></Button>
          </div>
        </header>
        <main id="portal-main" className="flex-1 overflow-y-auto p-5 sm:p-8">
          <div className="mx-auto max-w-6xl"><Outlet /></div>
        </main>
        <nav className="sticky bottom-0 z-30 grid grid-cols-5 border-t border-border bs-glass lg:hidden">
          {LINKS.slice(0, 5).map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end}
              className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[10px] ${isActive ? "text-primary" : "text-muted-foreground"}`}>
              <l.icon className="h-5 w-5" /> {l.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
